import { computed, ref } from 'vue'
import { defineStore } from 'pinia'
import axios from 'axios'
import { API_BASE } from '@/config/apiBase'

const POLL_INTERVAL_MS = 15000
const REQUEST_TIMEOUT_MS = 8000
const CACHE_KEY = 'maintenanceRuntimeCache'

export const useMaintenanceStore = defineStore('maintenance', () => {
  const runtime = ref(null)
  const serverOffsetMs = ref(0)
  const serverNowMs = ref(Date.now())
  const pollTimer = ref(null)
  const tickTimer = ref(null)
  const inited = ref(false)
  const runtimeFetchFailed = ref(false)

  const maintenance = computed(() => runtime.value?.maintenance || null)

  const maintenanceStartMs = computed(() => {
    const startAt = maintenance.value?.maintenanceStartAt
    if (!startAt) return null
    const ms = new Date(startAt).getTime()
    return Number.isNaN(ms) ? null : ms
  })

  const countdownSeconds = computed(() => {
    const startMs = maintenanceStartMs.value
    if (!startMs || isMaintenanceActive.value) return 0
    const delta = Math.ceil((startMs - serverNowMs.value) / 1000)
    return Math.max(0, delta)
  })

  const isMaintenanceActive = computed(() => {
    if (!maintenance.value) return false
    if (maintenance.value.active) return true
    const startMs = maintenanceStartMs.value
    // 后端短暂不可用时，如果已到维护时间，前端进入兜底维护态，避免用户误操作
    if (runtimeFetchFailed.value && startMs && serverNowMs.value >= startMs) {
      return true
    }
    return false
  })

  const syncServerTime = () => {
    serverNowMs.value = Date.now() + serverOffsetMs.value
  }

  const saveRuntimeCache = () => {
    try {
      if (!runtime.value) return
      localStorage.setItem(CACHE_KEY, JSON.stringify(runtime.value))
    } catch {
      // 忽略 localStorage 异常
    }
  }

  const loadRuntimeCache = () => {
    try {
      const raw = localStorage.getItem(CACHE_KEY)
      if (!raw) return
      const parsed = JSON.parse(raw)
      if (parsed && typeof parsed === 'object') {
        runtime.value = parsed
      }
    } catch {
      // 忽略缓存解析异常
    }
  }

  const fetchRuntime = async () => {
    try {
      const response = await axios.get(`${API_BASE}/announcements/runtime`, {
        timeout: REQUEST_TIMEOUT_MS
      })
      const body = response?.data
      if (!body || (body.code !== 0 && body.code !== 200)) {
        runtimeFetchFailed.value = true
        return
      }

      const data = body.data || {}
      runtime.value = data
      if (data.serverTime) {
        const serverMs = new Date(data.serverTime).getTime()
        if (!Number.isNaN(serverMs)) {
          serverOffsetMs.value = serverMs - Date.now()
          syncServerTime()
        }
      }
      runtimeFetchFailed.value = false
      saveRuntimeCache()
    } catch {
      runtimeFetchFailed.value = true
    }
  }

  const start = async () => {
    if (inited.value) return
    inited.value = true

    syncServerTime()
    loadRuntimeCache()
    await fetchRuntime()

    tickTimer.value = window.setInterval(() => {
      syncServerTime()
    }, 1000)

    pollTimer.value = window.setInterval(() => {
      fetchRuntime()
    }, POLL_INTERVAL_MS)
  }

  const stop = () => {
    if (tickTimer.value) {
      clearInterval(tickTimer.value)
      tickTimer.value = null
    }
    if (pollTimer.value) {
      clearInterval(pollTimer.value)
      pollTimer.value = null
    }
    inited.value = false
  }

  return {
    maintenance,
    isMaintenanceActive,
    countdownSeconds,
    fetchRuntime,
    start,
    stop
  }
})

